import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router";

import { Alert, LoadingBlock } from "../components/ui";
import { useAuth } from "../lib/auth";

/** UI-only guards for navigation. The API enforces every permission independently. */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <LoadingBlock />;
  if (!user)
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  return <>{children}</>;
}

export function RequireStaff({ admin, children }: { admin?: boolean; children: ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingBlock />;
  if (!user) return <Navigate to="/login" replace />;
  if (admin ? !user.is_admin : !user.is_staff)
    return (
      <Alert tone="error" title="Not available">
        You do not have access to this page.
      </Alert>
    );
  if (user.mfa_setup_required) return <Navigate to="/account/security" replace />;
  if (!user.mfa_verified)
    return (
      <Alert tone="warning" title="Verification needed">
        Please sign out and sign in again with your verification code.
      </Alert>
    );
  return <>{children}</>;
}

/** For guest-only pages (sign in, register). Signed-in users go where they were headed:
 * staff who still need two-step verification go to security settings first. */
export function RedirectIfAuthed({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <LoadingBlock />;
  if (user) {
    const from = (location.state as { from?: string } | null)?.from;
    const home = user.is_staff ? "/staff" : "/dashboard";
    const target = user.mfa_setup_required ? "/account/security" : (from ?? home);
    return <Navigate to={target} replace />;
  }
  return <>{children}</>;
}
